import { mensagemDeErro } from '@common/errors/mensagemDeErro';
import { Alert, Combobox, FormField, Input, Select, SelectItem } from '@ntconsult/aluma-react';
import type { UseQueryResult } from '@tanstack/react-query';
import { useState } from 'react';
import {
  type Control,
  Controller,
  type FieldErrors,
  type UseFormGetValues,
  type UseFormRegister,
  type UseFormSetFocus,
  type UseFormSetValue,
  useWatch,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useAplicarCep, useConsultaCep } from '../hooks';
import type { Cidade, Estado, Pais } from '../types/Formulario';
import { AvisoCep } from './AvisoCep';
import { BRASIL, mascararCepDoPais, type Valores } from './formulario.utils';

type Props = {
  control: Control<Valores>;
  register: UseFormRegister<Valores>;
  setValue: UseFormSetValue<Valores>;
  getValues: UseFormGetValues<Valores>;
  setFocus: UseFormSetFocus<Valores>;
  errors: FieldErrors<Valores>;
  paisId: string;
  estadoId: string;
  cidadeId: string;
  /** Nome da cidade já salva ou da fotografia: o gatilho do `Combobox` precisa dele com o painel fechado. */
  cidadeInicial: { id: string; nome: string } | null;
  paises: UseQueryResult<Pais[]>;
  estados: UseQueryResult<Estado[]>;
  cidades: UseQueryResult<Cidade[]>;
};

export function EnderecoOficialSection({
  control,
  register,
  setValue,
  getValues,
  setFocus,
  errors,
  paisId,
  estadoId,
  cidadeId,
  cidadeInicial,
  paises,
  estados,
  cidades,
}: Props) {
  const { t } = useTranslation(['formulario', 'erros', 'common']);
  const e = errors.oficial;
  const brasil = paisId === BRASIL;
  const falhaLista = paises.isError || estados.isError || cidades.isError;

  // Busca de CEP (R1, R16): estado e cidade entram nos Select/Combobox pelo id que a API casou.
  // Só no Brasil (spec 11 R8): fora dele o código postal é texto livre.
  const [cepTocado, setCepTocado] = useState(false);
  const consulta = useConsultaCep({
    cep: useWatch({ control, name: 'oficial.cep' }),
    habilitado: brasil && cepTocado,
  });
  useAplicarCep(consulta.endereco, getValues('oficial'), (d, escrever) => {
    const v = getValues('oficial');
    const set = (campo: 'endereco' | 'distrito' | 'estadoId' | 'cidadeId') => (x: string) =>
      setValue(`oficial.${campo}`, x, { shouldValidate: !!e?.[campo] });
    escrever('endereco', v.endereco, d.logradouro ?? '', set('endereco'));
    escrever('distrito', v.distrito, d.bairro ?? '', set('distrito'));
    // Estado e cidade andam juntos: a cidade só vale dentro do estado que a busca escolheu.
    if (d.estadoId != null) {
      const estadoNovo = String(d.estadoId);
      escrever('estadoId', v.estadoId, estadoNovo, set('estadoId'));
      const atual = getValues('oficial.estadoId');
      if (atual === estadoNovo) {
        const cidadeNova = d.cidadeId == null ? '' : String(d.cidadeId);
        // Estado trocado pela busca invalida a cidade anterior, mesmo editada.
        escrever('cidadeId', v.cidadeId, cidadeNova, set('cidadeId'), atual !== v.estadoId);
      }
    }
    setFocus('oficial.numero');
  });

  /** Trocar o país limpa os campos que não existem no outro modo (spec 11 §6.2). */
  function trocarPais(novo: string) {
    const eraBrasil = getValues('oficial.paisId') === BRASIL;
    setValue('oficial.paisId', novo, { shouldValidate: !!e?.paisId });
    if (eraBrasil === (novo === BRASIL)) return;
    setCepTocado(false);
    setValue('oficial.cep', '');
    if (eraBrasil) {
      setValue('oficial.estadoId', '');
      setValue('oficial.cidadeId', '');
    } else {
      setValue('oficial.cidade', '');
      setValue('oficial.regiao', '');
    }
  }

  const rotulos: Record<string, string> = {};
  if (consulta.endereco?.cidadeId)
    rotulos[String(consulta.endereco.cidadeId)] = consulta.endereco.cidade;
  if (cidadeInicial) rotulos[cidadeInicial.id] = cidadeInicial.nome;
  for (const c of cidades.data ?? []) rotulos[String(c.id)] = c.nome;

  return (
    // O título é o do passo "cadastro" (spec 33 R9); a caixa "desatualizado" e o atalho saíram (R8).
    <section className="flex flex-col gap-4" aria-label={t('formulario:oficial.titulo')}>
      <div className="flex flex-col gap-4">
        {falhaLista && (
          <Alert
            variant="danger"
            title={t(`erros:${mensagemDeErro(paises.error ?? estados.error ?? cidades.error)}`)}
          />
        )}
        {!paises.isError && (
          <FormField label={t('formulario:campos.pais')} error={e?.paisId?.message} required>
            <Select
              value={paisId}
              placeholder={t('formulario:selecione')}
              onValueChange={trocarPais}
            >
              {(paises.data ?? []).map((p) => (
                <SelectItem key={p.id} value={String(p.id)}>
                  {p.nome}
                </SelectItem>
              ))}
            </Select>
          </FormField>
        )}
        {brasil ? (
          <>
            {/* Em linhas, na ordem da spec 18 R10: CEP, Logradouro e Número; Complemento e Bairro. */}
            <div className="grid gap-4 sm:grid-cols-[9rem_1fr_7rem]">
              <Controller
                control={control}
                name="oficial.cep"
                render={({ field }) => (
                  <FormField label={t('formulario:campos.cep')} error={e?.cep?.message} required>
                    <Input
                      inputMode="numeric"
                      autoComplete="postal-code"
                      maxLength={9}
                      value={field.value}
                      onChange={(ev) => {
                        setCepTocado(true);
                        field.onChange(mascararCepDoPais(ev.target.value));
                      }}
                      onBlur={field.onBlur}
                      ref={field.ref}
                    />
                  </FormField>
                )}
              />
              <FormField
                label={t('formulario:campos.logradouro')}
                error={e?.endereco?.message}
                required
              >
                <Input
                  maxLength={300}
                  placeholder={t('formulario:campos.logradouroExemplo')}
                  {...register('oficial.endereco')}
                />
              </FormField>
              <FormField label={t('formulario:campos.numero')} error={e?.numero?.message} required>
                <Input maxLength={10} {...register('oficial.numero')} />
              </FormField>
            </div>
            <AvisoCep consulta={consulta} cidadeNaoCasou />
            <div className="grid gap-4 sm:grid-cols-2">
              <FormField label={t('formulario:campos.complemento')} error={e?.complemento?.message}>
                <Input
                  maxLength={200}
                  placeholder={t('formulario:campos.complementoExemplo')}
                  {...register('oficial.complemento')}
                />
              </FormField>
              <FormField
                label={t('formulario:campos.bairro')}
                error={e?.distrito?.message}
                required
              >
                <Input maxLength={100} {...register('oficial.distrito')} />
              </FormField>
            </div>
            <EstadoCidadeOficial
              setValue={setValue}
              errors={errors}
              estadoId={estadoId}
              cidadeId={cidadeId}
              rotulos={rotulos}
              estados={estados}
              cidades={cidades}
            />
          </>
        ) : (
          <>
            <FormField label={t('formulario:campos.codigoPostal')} error={e?.cep?.message}>
              <Input maxLength={30} autoComplete="postal-code" {...register('oficial.cep')} />
            </FormField>
            <FormField
              label={t('formulario:campos.endereco')}
              error={e?.endereco?.message}
              required
            >
              <Input maxLength={300} {...register('oficial.endereco')} />
            </FormField>
            <div className="grid gap-4 sm:grid-cols-2">
              <FormField label={t('formulario:campos.numeroOpcional')} error={e?.numero?.message}>
                <Input maxLength={10} {...register('oficial.numero')} />
              </FormField>
              <FormField label={t('formulario:campos.complemento')} error={e?.complemento?.message}>
                <Input
                  maxLength={200}
                  placeholder={t('formulario:campos.complementoExemplo')}
                  {...register('oficial.complemento')}
                />
              </FormField>
            </div>
            <FormField label={t('formulario:campos.bairroOpcional')} error={e?.distrito?.message}>
              <Input maxLength={100} {...register('oficial.distrito')} />
            </FormField>
            <div className="grid gap-4 sm:grid-cols-2">
              <FormField label={t('formulario:campos.cidade')} error={e?.cidade?.message} required>
                <Input
                  maxLength={255}
                  autoComplete="address-level2"
                  {...register('oficial.cidade')}
                />
              </FormField>
              <FormField label={t('formulario:campos.regiao')} error={e?.regiao?.message}>
                <Input
                  maxLength={100}
                  autoComplete="address-level1"
                  {...register('oficial.regiao')}
                />
              </FormField>
            </div>
            <p className="text-sm text-foreground-muted">{t('formulario:oficial.exteriorHint')}</p>
          </>
        )}
      </div>
    </section>
  );
}

/** Estado e Cidade do oficial no Brasil: no bloco do cadastro e no passo "mesmo" quando a cidade não casa (spec 33 R8). */
export function EstadoCidadeOficial({
  setValue,
  errors,
  estadoId,
  cidadeId,
  rotulos,
  estados,
  cidades,
  cidadeNaoCasou = false,
}: {
  setValue: UseFormSetValue<Valores>;
  errors: FieldErrors<Valores>;
  estadoId: string;
  cidadeId: string;
  rotulos: Record<string, string>;
  estados: UseQueryResult<Estado[]>;
  cidades: UseQueryResult<Cidade[]>;
  cidadeNaoCasou?: boolean;
}) {
  const { t } = useTranslation(['formulario', 'common']);
  const e = errors.oficial;
  return (
    <>
      <div className="grid gap-4 sm:grid-cols-2">
        <FormField label={t('formulario:campos.estado')} error={e?.estadoId?.message} required>
          <Select
            value={estadoId}
            placeholder={t('formulario:selecione')}
            onValueChange={(v) => {
              // Trocar o estado limpa a cidade (sem useEffect).
              setValue('oficial.estadoId', v, { shouldValidate: !!e?.estadoId });
              setValue('oficial.cidadeId', '');
            }}
          >
            {(estados.data ?? []).map((s) => (
              <SelectItem key={s.id} value={String(s.id)}>
                {s.nome}
              </SelectItem>
            ))}
          </Select>
        </FormField>
        <FormField label={t('formulario:campos.cidade')} error={e?.cidadeId?.message} required>
          <Combobox
            value={cidadeId}
            placeholder={t('formulario:selecione')}
            valueLabels={rotulos}
            disabled={!estadoId}
            onValueChange={(v) =>
              setValue('oficial.cidadeId', v, { shouldValidate: !!e?.cidadeId })
            }
          >
            <Combobox.Trigger />
            <Combobox.Content searchPlaceholder={t('common:searchPlaceholder')}>
              <Combobox.List>
                <Combobox.Empty>{t('formulario:oficial.semCidades')}</Combobox.Empty>
                {(cidades.data ?? []).map((c) => (
                  <Combobox.Option key={c.id} value={String(c.id)} keywords={[c.nome]}>
                    {c.nome}
                  </Combobox.Option>
                ))}
              </Combobox.List>
            </Combobox.Content>
          </Combobox>
        </FormField>
      </div>
      {cidadeNaoCasou && !cidadeId && (
        <p className="text-sm text-foreground-muted">{t('formulario:cep.cidadeNaoCasou')}</p>
      )}
      <p className="text-sm text-foreground-muted">{t('formulario:oficial.cidadeAusente')}</p>
    </>
  );
}
